import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/hooks';
import { selectTenant, setTenant } from '@/state/slices/userPreferenceSlice';

export enum Tenant {
  Seller = 'seller',
  Buyer = 'buyer',
  Zeus = 'zeus',
}

const TenantContext = createContext<Tenant | undefined>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const storedTenant = useAppSelector(selectTenant);

  const getTenantFromString = (tenantString: string | null): Tenant | null => {
    if (!tenantString) return null;
    if (['sell', 'seller'].includes(tenantString)) return Tenant.Seller;
    if (['buy', 'buyer'].includes(tenantString)) return Tenant.Buyer;
    if (tenantString === 'zeus') return Tenant.Zeus;
    return null;
  };

  const value = useMemo((): Tenant => {
    const hostname = window.location.hostname;

    // 1. Check URL parameter first (highest priority)
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    const tenantFromParam = getTenantFromString(tenantParam);
    if (tenantFromParam) {
      return tenantFromParam;
    }

    // 2. Check hostname-based detection
    const parts = hostname.split('.');
    const subdomain = parts[0] || null;
    const detectedTenant = getTenantFromString(subdomain);
    
    if (detectedTenant) {
      return detectedTenant;
    }

    // 3. Use stored tenant if no valid subdomain found
    if (storedTenant) {
      return storedTenant;
    }

    // 4. Default fallback
    return Tenant.Seller;
  }, [storedTenant]);

  // Update Redux store when tenant changes
  useEffect(() => {
    dispatch(setTenant(value));
  }, [value, dispatch]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = (): Tenant => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

